/**
 * Canto layout mode controller.
 *
 * Two modes:
 *   desktop — compact, mouse-optimised (current default)
 *   touch   — larger tap targets, keyboard-aware modals (iPad / touchscreen)
 *
 * Resolution order:
 *   1. localStorage ('canto-layout')
 *   2. pointer media query auto-detect (coarse = touch)
 *
 * Applies data-layout="desktop|touch" to <html>.
 * The early-apply script in index.html mirrors this logic to avoid a flash
 * of wrong layout on first paint.
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Types & storage helpers
// ---------------------------------------------------------------------------

export type LayoutMode = 'desktop' | 'touch';

const STORAGE_KEY = 'canto-layout';
const VALID: readonly LayoutMode[] = ['desktop', 'touch'];

export function detectDefaultMode(): LayoutMode {
  if (typeof window === 'undefined') return 'desktop';
  return window.matchMedia('(pointer: coarse)').matches ? 'touch' : 'desktop';
}

export function getStoredLayout(): LayoutMode | null {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(val as LayoutMode) ? (val as LayoutMode) : null;
  } catch {
    return null;
  }
}

export function applyLayout(mode: LayoutMode): void {
  document.documentElement.setAttribute('data-layout', mode);
}

function saveLayout(mode: LayoutMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore
  }
  applyLayout(mode);
}

export function resolveLayout(): LayoutMode {
  return getStoredLayout() ?? detectDefaultMode();
}

// ---------------------------------------------------------------------------
// React context
// ---------------------------------------------------------------------------

interface LayoutContextValue {
  mode: LayoutMode;
  toggle: () => void;
  isTouch: boolean;
}

const LayoutContext = createContext<LayoutContextValue>({
  mode: 'desktop',
  toggle: () => {},
  isTouch: false,
});

export function LayoutProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setMode] = useState<LayoutMode>(resolveLayout);

  // Sync data-layout attribute whenever mode changes.
  useEffect(() => {
    applyLayout(mode);
  }, [mode]);

  // Track virtual keyboard height via visualViewport API.
  // Sets --keyboard-height on :root; consumed by touch-mode modal CSS.
  useEffect(() => {
    function update(): void {
      const vv = window.visualViewport;
      const kbHeight = vv
        ? Math.max(0, window.innerHeight - vv.offsetTop - vv.height)
        : 0;
      document.documentElement.style.setProperty('--keyboard-height', `${kbHeight}px`);
    }
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    update();
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, []);

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next: LayoutMode = prev === 'desktop' ? 'touch' : 'desktop';
      saveLayout(next);
      return next;
    });
  }, []);

  return (
    <LayoutContext.Provider value={{ mode, toggle, isTouch: mode === 'touch' }}>
      {children as React.ReactElement}
    </LayoutContext.Provider>
  );
}

export function useLayout(): LayoutContextValue {
  return useContext(LayoutContext);
}
